import React, { useState, useEffect, useCallback } from "react";
import i18n from "i18next";
import { HiArrowPath, HiExclamationTriangle, HiXCircle } from "react-icons/hi2";
import { authedFetch } from "../../../../common/http_service";

/* eslint-disable react/prop-types */

/**
 * Gate that blocks the Video export popup until the training is
 * confirmed saved on the server. Two reasons it can block:
 *
 *   1. `props.isDirty === true` — the editor has unsaved local edits
 *      (Redux undoGroup dirty flag). User must click Save first.
 *   2. The server-side `exportedState` is missing/empty — the worker
 *      can't resolve the scene list. Same fix: Save.
 *
 * The check is cheap: GET /api/ideal-studio/training/<id> and inspect
 * `exportedState`. Cached in state for the lifetime of the gate; the
 * user can click "Yeniden Kontrol Et" to retry.
 */
const apiBase = () => (window.ideaLibraryRootUrl || "").replace(/\/$/, "");

export default function SaveRequiredGate({ trainingId, isDirty, onSave, children }) {
  const [status, setStatus] = useState("checking"); // checking | ok | needsSave | error
  const [errorMsg, setErrorMsg] = useState("");
  const [saving, setSaving] = useState(false);

  const check = useCallback(async () => {
    if (!trainingId) {
      setStatus("error");
      setErrorMsg(i18n.t("video_export.training_id_missing", "Training ID bulunamadı."));
      return;
    }
    if (isDirty) {
      setStatus("needsSave");
      return;
    }
    setStatus("checking");
    setErrorMsg("");
    try {
      const res = await authedFetch(
        `${apiBase()}/api/ideal-studio/training/${trainingId}?isTemplate=false`
      );
      if (!res.ok) throw new Error("Training bilgisi alınamadı.");
      const data = await res.json();
      // exportedState is the canonical CourseJsonModel snapshot the
      // worker reads. Missing/empty = nothing to render.
      const exportedState = data?.exportedState;
      if (!exportedState || (typeof exportedState === "string" && exportedState.trim() === "")) {
        setStatus("needsSave");
        return;
      }
      try {
        const parsed =
          typeof exportedState === "string" ? JSON.parse(exportedState) : exportedState;
        const present = parsed?.present ?? parsed;
        const ids = present?.navItemsIds;
        if (
          !Array.isArray(ids) ||
          ids.filter((id) => typeof id === "string" && id.startsWith("pa-")).length === 0
        ) {
          setStatus("needsSave");
          setErrorMsg(
            i18n.t(
              "video_export.no_scenes",
              "Bu eğitimin sahnesi yok. Lütfen düzenleyiciye dönün ve sahne ekleyin."
            )
          );
          return;
        }
      } catch (e) {
        setStatus("needsSave");
        setErrorMsg(
          `${i18n.t("video_export.state_read_error", "Kayıtlı durum okunamadı")}: ${e.message}`
        );
        return;
      }
      setStatus("ok");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.message);
    }
  }, [trainingId, isDirty]);

  useEffect(() => {
    check();
  }, [check]);

  // If dirty becomes false (user clicked save and the Redux flag flipped),
  // re-run the gate automatically.
  useEffect(() => {
    if (!isDirty && status === "needsSave") {
      check();
    }
  }, [isDirty, status, check]);

  if (status === "ok") {
    return <>{children}</>;
  }

  return (
    <div className="save-required-gate">
      {status === "checking" && (
        <div className="save-gate-card">
          <HiArrowPath className="spin" />
          <div className="save-gate-title">
            {i18n.t("video_export.checking_save", "Kayıt kontrol ediliyor…")}
          </div>
        </div>
      )}

      {status === "needsSave" && (
        <div className="save-gate-card save-gate-card--warn">
          <HiExclamationTriangle />
          <div className="save-gate-title">
            {i18n.t("video_export.save_required_title", "Önce kaydetmeniz gerekiyor")}
          </div>
          <div className="save-gate-body">
            {errorMsg ||
              i18n.t(
                "video_export.save_required_body",
                'Video üretmeden önce mevcut çalışmanızın sunucuda kayıtlı olması gerekiyor. Sahne, ses, animasyon ve metin değişikliklerinizin tümünün doğru render edilmesi için lütfen önce "Kaydet" deyin, sonra tekrar deneyin.'
              )}
          </div>
          <div className="save-gate-actions">
            {typeof onSave === "function" && (
              <button
                type="button"
                className="vexp-btn primary"
                disabled={saving}
                onClick={async () => {
                  setSaving(true);
                  try {
                    const result = onSave();
                    // Tolerate both sync and async save handlers.
                    if (result && typeof result.then === "function") {
                      await result;
                    } else {
                      // Sync save → give the editor a tick to flush its
                      // POST before we re-query the server.
                      await new Promise((r) => setTimeout(r, 800));
                    }
                    await check();
                  } catch (e) {
                    setErrorMsg(
                      `${i18n.t("video_export.save_failed", "Kayıt başarısız")}: ${e.message}`
                    );
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                {saving
                  ? i18n.t("video_export.saving", "Kaydediliyor…")
                  : i18n.t("video_export.save_now", "Şimdi Kaydet")}
              </button>
            )}
            <button type="button" className="vexp-btn" onClick={() => check()}>
              {i18n.t("video_export.recheck", "Yeniden Kontrol Et")}
            </button>
          </div>
        </div>
      )}

      {status === "error" && (
        <div className="save-gate-card save-gate-card--error">
          <HiXCircle />
          <div className="save-gate-title">
            {i18n.t("video_export.error_title", "Bir hata oluştu")}
          </div>
          <div className="save-gate-body">{errorMsg}</div>
          <button type="button" className="vexp-btn" onClick={check}>
            {i18n.t("video_export.retry", "Tekrar Dene")}
          </button>
        </div>
      )}
    </div>
  );
}
/* eslint-enable react/prop-types */
